import { CalculatorIcon, PlusIcon, TagIcon } from '@heroicons/react/20/solid';
import { ButtonV2, Text, TooltipV2 } from '@cimplico/components';
import { CustomWorksheetTemplateOverview } from '../../types/custom-worksheet-v2';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { Calculation } from '../../types/worksheet';
import { toastNotification } from '../../components/toast/Toast';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateCalculation } from '../../queries/use-create-calculations';

type NewCalculationTemplateCardProps = {
    template: CustomWorksheetTemplateOverview;
    disabled: boolean;
    calculation?: Calculation;
    onCreating: () => void;
    onCreate: () => void;
};

function NewCalculationTemplateCard({ template, disabled, onCreate, onCreating }: NewCalculationTemplateCardProps): JSX.Element {
    const { id } = useWorkpaperContext();
    const { shortId } = useFirmContext();
    const createQuery = useCreateCalculation();
    const navigate = useNavigate();
    function create(): void {
        onCreating();
        createQuery.mutate(
            {
                templateId: template.id,
            },
            {
                onSettled: () => {
                    onCreate();
                },
                onSuccess: ({ calculationId }) => {
                    if (calculationId) {
                        navigate(`/${shortId}/workpapers/${id}/calculations/${calculationId}`);
                    }
                    toastNotification('success', 'Successfully created calculation!');
                },
                onError: () => {
                    toastNotification('error', 'Something went wrong! Unable to create calculation');
                },
            }
        );
    }

    return (
        <div key={template.id} className="p-4 border rounded relative">
            <div className="flex flex-col h-full gap-2">
                <div className="flex items-center gap-1">
                    <CalculatorIcon className="h-4 w-4 text-green-600 shrink-0" />
                    <Text className="text-sm font-bold text-gray-800 ml-1">{template.name}</Text>
                </div>
                {template.description && (
                    <Text className="text-xs text-gray-500" lines={2}>
                        {template.description}
                    </Text>
                )}
                <div className="flex items-start gap-2 border-t pt-2 mt-auto">
                    {/* Add tags */}
                    <div>
                        <div className="flex flex-wrap gap-1 items-center">
                            <TagIcon className="h-4 w-4 text-gray-600" />
                            {template.tags.slice(0, 1).map((tag) => (
                                <label key={tag} className="text-xs text-gray-500">
                                    {tag}
                                </label>
                            ))}
                            {template.tags.length > 1 && (
                                <span className="-mt-1">
                                    <TooltipV2 message={template.tags.slice(1)}>
                                        <label className="text-xs text-gray-800">+{template.tags.length - 1}</label>
                                    </TooltipV2>
                                </span>
                            )}
                        </div>
                    </div>
                    <span className="ml-auto">
                        <ButtonV2
                            size="xs"
                            label="Add"
                            leftIcon={<PlusIcon />}
                            type="secondary"
                            onClick={create}
                            isDisabled={disabled}
                            isLoading={createQuery.isLoading}
                        />
                    </span>
                </div>
            </div>
        </div>
    );
}

export default NewCalculationTemplateCard;
